import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ModuleId, ShellPersona } from '../app-shell/moduleRegistry';

export type AssistantWidthMode = 'compact' | 'expanded' | 'half' | 'full';

export type ShellState = {
  version: typeof SHELL_STATE_VERSION;
  lastVisitedPath?: string;
  defaultModule?: ModuleId | null;
  navOpen: boolean;
  assistantOpen: boolean;
  assistantWidthMode: AssistantWidthMode;
  showEnvBanner: boolean;
  personaOverride?: ShellPersona | null;
  updatedAt: string;
};

export const SHELL_STATE_VERSION = 2 as const;

const nowIso = () => new Date().toISOString();

const initialState: ShellState = {
  version: SHELL_STATE_VERSION,
  lastVisitedPath: undefined,
  defaultModule: null,
  navOpen: false,
  assistantOpen: true,
  assistantWidthMode: 'compact',
  showEnvBanner: true,
  personaOverride: null,
  updatedAt: nowIso(),
};

const touch = (state: ShellState) => {
  state.updatedAt = nowIso();
};

export const shellSlice = createSlice({
  name: 'shell',
  initialState,
  reducers: {
    hydrateShellState: (state, action: PayloadAction<Partial<ShellState>>) => {
      const next = action.payload;
      state.lastVisitedPath = next.lastVisitedPath ?? state.lastVisitedPath;
      state.defaultModule = next.defaultModule ?? state.defaultModule;
      state.navOpen = next.navOpen ?? state.navOpen;
      state.assistantOpen = next.assistantOpen ?? state.assistantOpen;
      state.assistantWidthMode = next.assistantWidthMode ?? state.assistantWidthMode;
      state.showEnvBanner = next.showEnvBanner ?? state.showEnvBanner;
      state.personaOverride = next.personaOverride ?? state.personaOverride;
      touch(state);
    },
    setLastVisitedPath: (state, action: PayloadAction<string | undefined>) => {
      state.lastVisitedPath = action.payload;
      touch(state);
    },
    setDefaultModule: (state, action: PayloadAction<ModuleId | null>) => {
      state.defaultModule = action.payload;
      touch(state);
    },
    setNavOpen: (state, action: PayloadAction<boolean>) => {
      state.navOpen = action.payload;
      touch(state);
    },
    setAssistantOpen: (state, action: PayloadAction<boolean>) => {
      state.assistantOpen = action.payload;
      touch(state);
    },
    setAssistantWidthMode: (state, action: PayloadAction<AssistantWidthMode>) => {
      state.assistantWidthMode = action.payload;
      touch(state);
    },
    setShowEnvBanner: (state, action: PayloadAction<boolean>) => {
      state.showEnvBanner = action.payload;
      touch(state);
    },
    setPersonaOverride: (state, action: PayloadAction<ShellPersona | null>) => {
      state.personaOverride = action.payload;
      touch(state);
    },
    resetShellState: () => ({ ...initialState, updatedAt: nowIso() }),
  },
});

export const {
  hydrateShellState,
  setLastVisitedPath,
  setDefaultModule,
  setNavOpen,
  setAssistantOpen,
  setAssistantWidthMode,
  setShowEnvBanner,
  setPersonaOverride,
  resetShellState,
} = shellSlice.actions;
